/**
 * cdn-domain-origin-tab.tsx — Origin/Upstream configuration tab (act-as).
 *
 * Mirrors Partner's OriginTab.tsx: HTTP/S3 mode toggle, origin servers field-array,
 * connection settings, origin headers, TLS-to-origin, circuit breaker, origin shield,
 * retry policy. Mutations route through ActAsMutationConfirm.
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/upstream
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, useWatch, Controller, type Control } from 'react-hook-form';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNUpstreamFull,
  useDeleteActAsCDNUpstream,
  actAsCDNCacheKeys,
  type UpstreamConfig,
} from '@/api/act-as-cdn-cache';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

/* ── Form model ──────────────────────────────────────────────────────────── */

interface UrlEntry { value: string }
interface HeaderEntry { name: string; value: string }

interface UpstreamFormValues {
  mode: 'http' | 's3';
  urls: UrlEntry[];
  selector: string;
  protocol: string;
  maxRetries: string;
  connectionPoolSize: string;
  hostHeader: string;
  sniOverride: string;
  originHeaders: HeaderEntry[];
  tlsToOrigin: boolean;
  originCertId: string;
  originTlsVerify: boolean;
  circuitBreakerEnabled: boolean;
  circuitBreakerThreshold: string;
  circuitBreakerRecoverySecs: string;
  circuitBreakerHalfOpen: string;
  originShield: string;
  originShieldTls: boolean;
  retryOnStatus: string;
  retryOnConnectFailure: boolean;
  retryOnTimeout: boolean;
  retryOnReset: boolean;
  // S3
  s3Service: string;
  s3Bucket: string;
  s3Region: string;
  s3AccessKeyId: string;
  s3SecretAccessKey: string;
  s3AddressingStyle: string;
  s3Endpoint: string;
}

const DEFAULTS: UpstreamFormValues = {
  mode: 'http',
  urls: [{ value: '' }],
  selector: 'round_robin',
  protocol: 'h1.1',
  maxRetries: '0',
  connectionPoolSize: '0',
  hostHeader: '',
  sniOverride: '',
  originHeaders: [],
  tlsToOrigin: false,
  originCertId: '',
  originTlsVerify: true,
  circuitBreakerEnabled: false,
  circuitBreakerThreshold: '5',
  circuitBreakerRecoverySecs: '30',
  circuitBreakerHalfOpen: '2',
  originShield: '',
  originShieldTls: false,
  retryOnStatus: '',
  retryOnConnectFailure: true,
  retryOnTimeout: true,
  retryOnReset: false,
  s3Service: 'aws',
  s3Bucket: '',
  s3Region: 'us-east-1',
  s3AccessKeyId: '',
  s3SecretAccessKey: '',
  s3AddressingStyle: 'virtual_hosted',
  s3Endpoint: '',
};

const SELECTOR_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'failover', label: 'Failover' },
  { value: 'weighted_round_robin', label: 'Weighted Round Robin' },
];
const PROTOCOL_OPTIONS = [
  { value: 'h1.1', label: 'HTTP/1.1' },
  { value: 'h2', label: 'HTTP/2' },
];
const S3_PROVIDER_OPTIONS = [
  { value: 'aws', label: 'Amazon S3' },
  { value: 'wasabi', label: 'Wasabi' },
  { value: 'minio', label: 'MinIO' },
  { value: 'gcs', label: 'Google Cloud Storage' },
  { value: 'other', label: 'Other S3-compatible' },
];
const ADDRESSING_OPTIONS = [
  { value: 'virtual_hosted', label: 'Virtual-hosted style' },
  { value: 'path', label: 'Path style' },
];

function buildFormFromServer(cfg: UpstreamConfig | null): UpstreamFormValues {
  if (!cfg) return DEFAULTS;
  return {
    mode: cfg.mode ?? 'http',
    urls: cfg.urls?.length ? cfg.urls.map((v) => ({ value: v })) : [{ value: '' }],
    selector: cfg.selector ?? 'round_robin',
    protocol: cfg.protocol ?? 'h1.1',
    maxRetries: String(cfg.max_retries ?? 0),
    connectionPoolSize: String(cfg.connection_pool_size ?? 0),
    hostHeader: cfg.host_header ?? '',
    sniOverride: cfg.sni_override ?? '',
    originHeaders: cfg.origin_headers ?? [],
    tlsToOrigin: cfg.tls_to_origin ?? false,
    originCertId: cfg.origin_cert_id ?? '',
    originTlsVerify: cfg.origin_tls_verify ?? true,
    circuitBreakerEnabled: cfg.circuit_breaker_enabled ?? false,
    circuitBreakerThreshold: String(cfg.circuit_breaker_threshold ?? 5),
    circuitBreakerRecoverySecs: String(cfg.circuit_breaker_recovery_secs ?? 30),
    circuitBreakerHalfOpen: String(cfg.circuit_breaker_half_open ?? 2),
    originShield: cfg.origin_shield ?? '',
    originShieldTls: cfg.origin_shield_tls ?? false,
    retryOnStatus: cfg.retry_on_status ?? '',
    retryOnConnectFailure: cfg.retry_on_connect_failure ?? true,
    retryOnTimeout: cfg.retry_on_timeout ?? true,
    retryOnReset: cfg.retry_on_reset ?? false,
    s3Service: cfg.s3_service ?? 'aws',
    s3Bucket: cfg.s3_bucket ?? '',
    s3Region: cfg.s3_region ?? 'us-east-1',
    s3AccessKeyId: cfg.s3_access_key_id ?? '',
    s3SecretAccessKey: '',  // Never pre-fill secrets.
    s3AddressingStyle: cfg.s3_addressing_style ?? 'virtual_hosted',
    s3Endpoint: cfg.s3_endpoint ?? '',
  };
}

function buildPayload(v: UpstreamFormValues): UpstreamConfig {
  const base: UpstreamConfig = {
    mode: v.mode,
    circuit_breaker_enabled: v.circuitBreakerEnabled,
    origin_shield: v.originShield || undefined,
    origin_shield_tls: v.originShieldTls,
    retry_on_status: v.retryOnStatus || undefined,
    retry_on_connect_failure: v.retryOnConnectFailure,
    retry_on_timeout: v.retryOnTimeout,
    retry_on_reset: v.retryOnReset,
  };
  if (v.circuitBreakerEnabled) {
    base.circuit_breaker_threshold = Number(v.circuitBreakerThreshold) || 5;
    base.circuit_breaker_recovery_secs = Number(v.circuitBreakerRecoverySecs) || 30;
    base.circuit_breaker_half_open = Number(v.circuitBreakerHalfOpen) || 2;
  }
  if (v.mode === 'http') {
    const urls = v.urls.map((u) => u.value.trim()).filter(Boolean);
    return {
      ...base,
      urls,
      selector: v.selector,
      protocol: v.protocol,
      max_retries: Number(v.maxRetries) || 0,
      connection_pool_size: Number(v.connectionPoolSize) || 0,
      host_header: v.hostHeader || undefined,
      sni_override: v.sniOverride || undefined,
      origin_headers: v.originHeaders.filter((h) => h.name.trim()),
      tls_to_origin: v.tlsToOrigin,
      origin_cert_id: v.tlsToOrigin && v.originCertId ? v.originCertId : undefined,
      origin_tls_verify: v.tlsToOrigin ? v.originTlsVerify : undefined,
    };
  }
  // S3 mode
  return {
    ...base,
    s3_service: v.s3Service,
    s3_bucket: v.s3Bucket,
    s3_region: v.s3Region,
    s3_access_key_id: v.s3AccessKeyId || undefined,
    s3_secret_access_key: v.s3SecretAccessKey || undefined,
    s3_addressing_style: v.s3AddressingStyle,
    s3_endpoint: v.s3Endpoint || undefined,
    origin_headers: v.originHeaders.filter((h) => h.name.trim()),
  };
}

/* ── Small RHF helpers ───────────────────────────────────────────────────── */

type Ctl = Control<UpstreamFormValues>;

function TF({
  control,
  name,
  label,
  description,
  placeholder,
  type,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues | `urls.${number}.value` | `originHeaders.${number}.${'name' | 'value'}`;
  label?: string;
  description?: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <Field label={label} description={description}>
          {(props) => (
            <Input
              {...props}
              type={type}
              placeholder={placeholder}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
            />
          )}
        </Field>
      )}
    />
  );
}

const SELECT_NONE = '__none__';
const toSV = (v: string) => (v === '' ? SELECT_NONE : v);
const fromSV = (v: string) => (v === SELECT_NONE ? '' : v);

function SF({
  control,
  name,
  label,
  description,
  options,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues;
  label: string;
  description?: string;
  options: { value: string; label: string }[];
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <Field label={label} description={description}>
          {(props) => (
            <Select
              value={toSV(field.value as string)}
              onValueChange={(v) => field.onChange(fromSV(v))}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={toSV(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}
    />
  );
}

function SW({
  control,
  name,
  title,
  description,
  invert,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues;
  title: string;
  description?: string;
  invert?: boolean;
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <SettingRow
          title={title}
          description={description}
          control={
            <Switch
              checked={invert ? !field.value : !!field.value}
              onCheckedChange={(v) => field.onChange(invert ? !v : v)}
            />
          }
        />
      )}
    />
  );
}

const grid2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
};

/* ── Origin URL field array ──────────────────────────────────────────────── */

function OriginServers({ control }: { control: Ctl }) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ control, name: 'urls' });
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {fields.map((f, idx) => (
        <div key={f.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Controller
            control={control}
            name={`urls.${idx}.value`}
            render={({ field }) => (
              <Input {...field} placeholder="http://10.0.0.1:8080" style={{ flex: 1 }} />
            )}
          />
          {fields.length > 1 && (
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t('cdn_domain.origin_tab.remove_origin', 'Remove origin')}
              onClick={() => remove(idx)}
            >
              <Trash2 size={15} />
            </IconButton>
          )}
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="sm"
          startIcon={<Plus size={14} />}
          onClick={() => append({ value: '' })}
        >
          {t('cdn_domain.origin_tab.add_origin', 'Add origin')}
        </Button>
      </div>
    </div>
  );
}

/* ── Origin headers field array ──────────────────────────────────────────── */

function OriginHeaders({ control }: { control: Ctl }) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ control, name: 'originHeaders' });
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {fields.map((f, idx) => (
        <div key={f.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Controller
            control={control}
            name={`originHeaders.${idx}.name`}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('cdn_domain.origin_tab.header_name_placeholder', 'Header name')}
                style={{ flex: 1 }}
              />
            )}
          />
          <Controller
            control={control}
            name={`originHeaders.${idx}.value`}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('cdn_domain.origin_tab.header_value_placeholder', 'Header value')}
                style={{ flex: 2 }}
              />
            )}
          />
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('cdn_domain.origin_tab.remove_header', 'Remove header')}
            onClick={() => remove(idx)}
          >
            <Trash2 size={15} />
          </IconButton>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="sm"
          startIcon={<Plus size={14} />}
          onClick={() => append({ name: '', value: '' })}
        >
          {t('cdn_domain.origin_tab.add_header', 'Add header')}
        </Button>
      </div>
    </div>
  );
}

/* ── Common cards (circuit breaker, shield, retry) ───────────────────────── */

function CommonCards({ control }: { control: Ctl }) {
  const { t } = useTranslation();
  const cbEnabled = useWatch({ control, name: 'circuitBreakerEnabled' });
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.origin_tab.section_circuit_breaker', 'Circuit breaker')}</CardTitle>
          <CardDescription>
            {t('cdn_domain.origin_tab.section_circuit_breaker_desc', 'Trip the circuit when an origin keeps failing.')}
          </CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SW control={control} name="circuitBreakerEnabled" title={t('cdn_domain.origin_tab.field_cb_enabled', 'Enable circuit breaker')} />
          {cbEnabled && (
            <div style={grid2}>
              <TF
                control={control}
                name="circuitBreakerThreshold"
                label={t('cdn_domain.origin_tab.field_cb_threshold', 'Failure threshold')}
                type="number"
                description={t('cdn_domain.origin_tab.field_cb_threshold_desc', 'Consecutive failures before tripping.')}
              />
              <TF
                control={control}
                name="circuitBreakerRecoverySecs"
                label={t('cdn_domain.origin_tab.field_cb_recovery', 'Recovery interval (s)')}
                type="number"
                description={t('cdn_domain.origin_tab.field_cb_recovery_desc', 'Wait in Open state before testing origin.')}
              />
              <TF
                control={control}
                name="circuitBreakerHalfOpen"
                label={t('cdn_domain.origin_tab.field_cb_half_open', 'Half-open probes')}
                type="number"
                description={t('cdn_domain.origin_tab.field_cb_half_open_desc', 'Successful probes required to fully close.')}
              />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.origin_tab.section_shield', 'Origin shield')}</CardTitle>
          <CardDescription>
            {t('cdn_domain.origin_tab.section_shield_desc', 'An intermediate shielding server that protects the origin from direct peak traffic.')}
          </CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TF
            control={control}
            name="originShield"
            label={t('cdn_domain.origin_tab.field_shield_addr', 'Origin shield address')}
            placeholder="shield.example.com:443"
          />
          <SW
            control={control}
            name="originShieldTls"
            title={t('cdn_domain.origin_tab.field_shield_tls', 'Use TLS for shield connections')}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.origin_tab.section_retry', 'Retry policy')}</CardTitle>
          <CardDescription>
            {t('cdn_domain.origin_tab.section_retry_desc', 'Which error conditions trigger an automatic retry to the next upstream origin.')}
          </CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TF
            control={control}
            name="retryOnStatus"
            label={t('cdn_domain.origin_tab.field_retry_status', 'Retry on status codes')}
            placeholder="502, 503, 504"
            description={t('cdn_domain.origin_tab.field_retry_status_desc', 'Comma-separated HTTP codes.')}
          />
          <SW control={control} name="retryOnConnectFailure" title={t('cdn_domain.origin_tab.field_retry_connect', 'Retry on connect failure')} />
          <SW control={control} name="retryOnTimeout" title={t('cdn_domain.origin_tab.field_retry_timeout', 'Retry on timeout')} />
          <SW control={control} name="retryOnReset" title={t('cdn_domain.origin_tab.field_retry_reset', 'Retry on connection reset')} />
        </CardBody>
      </Card>
    </>
  );
}

/* ── Tab ─────────────────────────────────────────────────────────────────── */

interface CdnDomainOriginTabProps {
  domainId: string;
}

export function CdnDomainOriginTab({ domainId }: CdnDomainOriginTabProps) {
  const { t } = useTranslation();
  const upstreamQ = useActAsCDNUpstreamFull(domainId);
  const deleteM = useDeleteActAsCDNUpstream(domainId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Derive the core config — source of truth for the form.
  const fanOut = upstreamQ.data;
  const cfg = fanOut?.core ?? fanOut?.config ?? null;
  const isConfigured = !!(cfg && (cfg.urls?.length || cfg.mode === 's3'));

  const coreReadFailed =
    !upstreamQ.isLoading &&
    fanOut?.vendors &&
    fanOut.coreProvider &&
    (fanOut.vendors[fanOut.coreProvider]?.status === 'error');

  const { control, handleSubmit, reset, watch, setValue, formState } =
    useForm<UpstreamFormValues>({ defaultValues: DEFAULTS });
  const mode = watch('mode');
  const tlsToOrigin = watch('tlsToOrigin');

  useEffect(() => {
    if (upstreamQ.isLoading) return;
    reset(buildFormFromServer(cfg));
  }, [upstreamQ.isLoading, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture validated form values for the confirm dialog.
  const [pendingPayload, setPendingPayload] = useState<UpstreamConfig | null>(null);

  function onSubmit(values: UpstreamFormValues) {
    const payload = buildPayload(values);
    setPendingPayload(payload);
    setConfirmOpen(true);
  }

  if (upstreamQ.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (upstreamQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.origin_tab.load_error', 'Failed to load origin configuration')}
        onRetry={() => upstreamQ.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.origin_tab.title', 'Origin')}
      description={t('cdn_domain.origin_tab.description', 'Backend origin configuration for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {coreReadFailed && (
          <Alert tone="warning" title={t('cdn_domain.origin_tab.core_read_failed', 'Core configuration could not be read')}>
            {t('cdn_domain.origin_tab.core_read_failed_desc', "The core provider didn't return the origin configuration. Values below may be stale — saving will still write to the core configuration.")}
          </Alert>
        )}

        {/* ── Mode + origin servers / S3 ── */}
        <Card>
          <CardHeader
            actions={
              <Controller
                control={control}
                name="mode"
                render={({ field }) => (
                  <SegmentedControl
                    items={[
                      { value: 'http', label: t('cdn_domain.origin_tab.mode_http', 'HTTP Origin') },
                      { value: 's3', label: t('cdn_domain.origin_tab.mode_s3', 'S3 / Object Storage') },
                    ]}
                    value={field.value}
                    onValueChange={(v) => field.onChange(v as 'http' | 's3')}
                    aria-label={t('cdn_domain.origin_tab.mode_label', 'Origin mode')}
                  />
                )}
              />
            }
          >
            <CardTitle>
              {mode === 'http'
                ? t('cdn_domain.origin_tab.section_http_servers', 'Origin servers')
                : t('cdn_domain.origin_tab.section_s3_origin', 'S3-compatible origin')}
            </CardTitle>
            <CardDescription>
              {t('cdn_domain.origin_tab.section_backend_desc', 'The backend the CDN pulls content from.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {mode === 'http' ? (
              <OriginServers control={control} />
            ) : (
              <S3Fields control={control} setValue={(n, v) => setValue(n, v as never, { shouldDirty: true })} />
            )}
          </CardBody>
        </Card>

        {/* HTTP-only sections */}
        {mode === 'http' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.origin_tab.section_connection', 'Connection settings')}</CardTitle>
              </CardHeader>
              <CardBody>
                <div style={grid2}>
                  <SF control={control} name="selector" label={t('cdn_domain.origin_tab.field_lb_algo', 'Load balancing algorithm')} options={SELECTOR_OPTIONS} />
                  <SF control={control} name="protocol" label={t('cdn_domain.origin_tab.field_protocol', 'Protocol')} options={PROTOCOL_OPTIONS} />
                  <TF control={control} name="maxRetries" label={t('cdn_domain.origin_tab.field_max_retries', 'Max retries')} type="number" description={t('cdn_domain.origin_tab.field_max_retries_desc', '0 = no retries.')} />
                  <TF control={control} name="connectionPoolSize" label={t('cdn_domain.origin_tab.field_pool_size', 'Connection pool size')} type="number" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.origin_tab.section_headers', 'Origin headers')}</CardTitle>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <div style={grid2}>
                  <TF control={control} name="hostHeader" label={t('cdn_domain.origin_tab.field_host_header', 'Host header override')} placeholder="origin.example.com" description={t('cdn_domain.origin_tab.field_host_header_desc', 'Empty = use the CDN domain name.')} />
                  <TF control={control} name="sniOverride" label={t('cdn_domain.origin_tab.field_sni', 'SNI override')} description={t('cdn_domain.origin_tab.field_sni_desc', 'Empty = auto-detect from origin URL.')} />
                </div>
                <div>
                  <CardTitle style={{ marginBottom: 'var(--vn-space-2)' }}>
                    {t('cdn_domain.origin_tab.section_custom_headers', 'Custom headers to origin')}
                  </CardTitle>
                  <OriginHeaders control={control} />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.origin_tab.section_tls', 'TLS to origin')}</CardTitle>
                <CardDescription>
                  {t('cdn_domain.origin_tab.section_tls_desc', 'Encrypt connections from edge nodes to your origin servers.')}
                </CardDescription>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <SW control={control} name="tlsToOrigin" title={t('cdn_domain.origin_tab.field_tls_to_origin', 'Use TLS for origin connections')} />
                {tlsToOrigin && (
                  <Alert tone="warning" icon={<AlertTriangle size={16} />}>
                    <SW
                      control={control}
                      name="originTlsVerify"
                      invert
                      title={t('cdn_domain.origin_tab.field_skip_verify', 'Skip certificate verification')}
                      description={t('cdn_domain.origin_tab.field_skip_verify_desc', 'Enable for origins with self-signed certs. Disables TLS certificate + hostname verification.')}
                    />
                  </Alert>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {/* S3-only origin headers */}
        {mode === 's3' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('cdn_domain.origin_tab.section_headers', 'Origin headers')}</CardTitle>
            </CardHeader>
            <CardBody>
              <OriginHeaders control={control} />
            </CardBody>
          </Card>
        )}

        <CommonCards control={control} />

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
                {t('cdn_domain.origin_tab.action_remove', 'Remove')}
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button
                  variant="secondary"
                  disabled={!formState.isDirty}
                  onClick={() => reset(buildFormFromServer(cfg))}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              )}
              <Button
                disabled={isConfigured && !formState.isDirty}
                onClick={handleSubmit(onSubmit)}
              >
                {isConfigured
                  ? t('cdn_domain.origin_tab.action_save', 'Save')
                  : t('cdn_domain.origin_tab.action_create', 'Create')}
              </Button>
            </>
          }
        />
      </div>

      {/* Save confirm */}
      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isConfigured
            ? t('cdn_domain.origin_tab.confirm_save_title', 'Save origin configuration')
            : t('cdn_domain.origin_tab.confirm_create_title', 'Create origin configuration')
        }
        summary={t(
          'cdn_domain.origin_tab.save_summary',
          'Update backend origin settings — URLs, connection parameters, TLS, and retry policy.',
        )}
        queryKeysToInvalidate={[actAsCDNCacheKeys.upstream(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/upstream`, pendingPayload)}
        onSuccess={() => reset(buildFormFromServer(pendingPayload as UpstreamConfig))}
      />

      {/* Delete confirm */}
      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.origin_tab.confirm_delete_title', 'Remove origin configuration')}
        summary={t(
          'cdn_domain.origin_tab.delete_summary',
          'Remove the custom origin configuration. The domain will lose its upstream settings.',
        )}
        queryKeysToInvalidate={[actAsCDNCacheKeys.upstream(domainId)]}
        mutationFn={() => deleteM.mutateAsync()}
        onSuccess={() => reset(DEFAULTS)}
        destructive
        confirmLabel={t('cdn_domain.origin_tab.action_remove', 'Remove')}
      />
    </Section>
  );
}

/* ── S3 fields sub-component ─────────────────────────────────────────────── */

function S3Fields({
  control,
  setValue,
}: {
  control: Ctl;
  setValue: (n: keyof UpstreamFormValues, v: string) => void;
}) {
  const { t } = useTranslation();
  const v = useWatch({ control });

  function onProvider(service: string) {
    setValue('s3Service', service);
    // Auto-fill endpoint defaults per provider.
    if (service === 'wasabi') {
      setValue('s3Endpoint', 's3.wasabisys.com');
      setValue('s3AddressingStyle', 'path');
    } else if (service === 'gcs') {
      setValue('s3Endpoint', 'storage.googleapis.com');
      setValue('s3AddressingStyle', 'path');
    } else if (service === 'aws') {
      setValue('s3Endpoint', '');
      setValue('s3AddressingStyle', 'virtual_hosted');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Controller
        control={control}
        name="s3Service"
        render={({ field }) => (
          <Field
            label={t('cdn_domain.origin_tab.field_s3_provider', 'Provider')}
            description={t('cdn_domain.origin_tab.field_s3_provider_desc', 'Auto-fills endpoint and region defaults.')}
          >
            {(props) => (
              <Select value={field.value} onValueChange={onProvider}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {S3_PROVIDER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
      />
      <div style={grid2}>
        <TF control={control} name="s3Bucket" label={t('cdn_domain.origin_tab.field_s3_bucket', 'Bucket name')} placeholder="my-cdn-assets" />
        <TF control={control} name="s3Region" label={t('cdn_domain.origin_tab.field_s3_region', 'Region')} placeholder="us-east-1" />
        <TF control={control} name="s3AccessKeyId" label={t('cdn_domain.origin_tab.field_s3_key_id', 'Access key ID')} placeholder="AKIA…" />
        <TF
          control={control}
          name="s3SecretAccessKey"
          label={t('cdn_domain.origin_tab.field_s3_secret', 'Secret access key')}
          type="password"
          placeholder="••••••••"
          description={t('cdn_domain.origin_tab.field_s3_secret_desc', 'Required on every save — stored secrets are never displayed.')}
        />
      </div>
      <SF
        control={control}
        name="s3AddressingStyle"
        label={t('cdn_domain.origin_tab.field_s3_addressing', 'Addressing style')}
        options={ADDRESSING_OPTIONS}
      />
      <TF
        control={control}
        name="s3Endpoint"
        label={t('cdn_domain.origin_tab.field_s3_endpoint', 'Custom endpoint (optional)')}
        description={t('cdn_domain.origin_tab.field_s3_endpoint_desc', 'Override the default endpoint for custom S3-compatible providers.')}
        placeholder="s3.us-east-1.amazonaws.com"
      />
      {/* S3 URL preview */}
      <Card flat>
        <CardBody style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          <b style={{ color: 'var(--vn-fg-default)' }}>
            {t('cdn_domain.origin_tab.s3_url_preview', 'Origin URL:')}
          </b>{' '}
          {buildS3UrlPreview({
            bucket: (v.s3Bucket as string) ?? '',
            region: (v.s3Region as string) ?? '',
            endpoint: (v.s3Endpoint as string) ?? '',
            addressingStyle: (v.s3AddressingStyle as string) ?? 'virtual_hosted',
          })}
        </CardBody>
      </Card>
    </div>
  );
}

function buildS3UrlPreview({
  bucket,
  region,
  endpoint,
  addressingStyle,
}: {
  bucket: string;
  region: string;
  endpoint: string;
  addressingStyle: string;
}): string {
  if (!bucket) return '—';
  const host = endpoint || `s3.${region || 'us-east-1'}.amazonaws.com`;
  if (addressingStyle === 'path') return `https://${host}/${bucket}`;
  return `https://${bucket}.${host}`;
}
